import React, { useRef, useState } from "react";

/**
 * Fullscreen Isometric aerial map viewer with drag-pan and wheel-zoom.
 * Image fills the viewport via object-fit: cover. Pan/zoom applies to a wrapper.
 */
export default function IsoViewer({ imageUrl, isNight }) {
    const wrapperRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const dragRef = useRef({ active: false, lx: 0, ly: 0 });

    const onMouseDown = (e) => {
        dragRef.current = { active: true, lx: e.clientX, ly: e.clientY };
    };
    const onMouseMove = (e) => {
        if (!dragRef.current.active) return;
        const dx = e.clientX - dragRef.current.lx;
        const dy = e.clientY - dragRef.current.ly;
        dragRef.current.lx = e.clientX;
        dragRef.current.ly = e.clientY;
        setPos((p) => ({ x: p.x + dx, y: p.y + dy }));
    };
    const onMouseUp = () => { dragRef.current.active = false; };

    const onWheel = (e) => {
        e.preventDefault();
        const delta = -e.deltaY * 0.0015;
        setScale((s) => Math.min(3, Math.max(0.6, s + delta)));
    };

    const resetView = () => { setScale(1); setPos({ x: 0, y: 0 }); };

    return (
        <div
            ref={wrapperRef}
            data-testid="iso-viewer"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
            onDoubleClick={resetView}
            className="absolute inset-0 w-full h-full overflow-hidden cursor-grab active:cursor-grabbing select-none"
            style={{
                background: isNight
                    ? "radial-gradient(ellipse at center, #0a0a14 0%, #02020a 70%)"
                    : "radial-gradient(ellipse at center, #f5f0e2 0%, #cfc7b5 100%)",
            }}
        >
            {/* Pan/zoom wrapper: full viewport, transformed */}
            <div
                className="absolute inset-0 fade-in-slow"
                style={{
                    transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                    transformOrigin: "center center",
                    transition: dragRef.current.active ? "none" : "transform 0.18s ease-out",
                }}
            >
                <img
                    src={imageUrl}
                    alt="Isometric estate view"
                    draggable={false}
                    className="block w-full h-full select-none pointer-events-none"
                    style={{
                        objectFit: "cover",
                        objectPosition: "center",
                        imageRendering: "high-quality",
                        filter: isNight
                            ? "contrast(1.06) saturate(1.05) drop-shadow(0 30px 80px rgba(0,0,0,0.6))"
                            : "contrast(1.04) saturate(1.03) drop-shadow(0 20px 60px rgba(0,0,0,0.25))",
                    }}
                />
            </div>
        </div>
    );
}
